/**
 * Generated by orval 🍺
 * Do not edit manually.
 * ILP Content Delivery API
 * API for delivering educational content including course structures and glossaries
 * OpenAPI spec version: v1
 */
import useSwr from "swr";
import type { Key, SWRConfiguration } from "swr";

import useSWRMutation from "swr/mutation";
import type { SWRMutationConfiguration } from "swr/mutation";

import type {
  CreateValidationRequest,
  Error,
  ListValidationsParams,
  Validation,
  ValidationAccepted,
} from "../model";

import { apiFetch } from "../../apiFetch";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

export const getCreateValidationUrl = () => {
  return `/api/v1/validations`;
};

/**
 * Accepts a learner's answer to one quiz slot (contentElementId + entryIndex) and grades it in the background: poll GET /api/v1/validations/{id} until its status is no longer pending. A retry of the same slot replaces the previous submission. Some types cap the submissions per slot and refuse further ones with 422.
 * @summary Submit an answer for grading
 */
export const createValidation = async (
  createValidationRequest?: CreateValidationRequest,
  options?: Parameters<typeof apiFetch>[1]
): Promise<ValidationAccepted> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string]
        )
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<ValidationAccepted>(getCreateValidationUrl(), {
    ...options,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getHeaders(options?.headers),
    },
    body: JSON.stringify(createValidationRequest),
  });
};

export const getCreateValidationMutationFetcher = (
  options?: SecondParameter<typeof apiFetch>
) => {
  return (_: Key, { arg }: { arg: CreateValidationRequest | undefined }) => {
    return createValidation(arg, options);
  };
};
export const getCreateValidationMutationKey = () =>
  [`/api/v1/validations`] as const;

export type CreateValidationMutationResult = NonNullable<
  Awaited<ReturnType<typeof createValidation>>
>;

/**
 * @summary Submit an answer for grading
 */
export const useCreateValidation = <TError = void | Error>(options?: {
  swr?: SWRMutationConfiguration<
    Awaited<ReturnType<typeof createValidation>>,
    TError,
    Key,
    CreateValidationRequest | undefined,
    Awaited<ReturnType<typeof createValidation>>
  > & { swrKey?: string };
  request?: SecondParameter<typeof apiFetch>;
}) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const swrKey = swrOptions?.swrKey ?? getCreateValidationMutationKey();
  const swrFn = getCreateValidationMutationFetcher(requestOptions);

  const query = useSWRMutation(swrKey, swrFn, swrOptions);

  return {
    swrKey,
    ...query,
  };
};
export const getListValidationsUrl = (params: ListValidationsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/validations?${stringifiedParams}`
    : `/api/v1/validations`;
};

/**
 * The learner's submissions within one subchapter of the enrollment, with their verdicts, so that a reopened lesson shows the quiz state again. Verdicts of the course completion quiz are withheld until the quiz is over.
 * @summary List the validations of a subchapter
 */
export const listValidations = async (
  params: ListValidationsParams,
  options?: Parameters<typeof apiFetch>[1]
): Promise<Validation[]> => {
  return apiFetch<Validation[]>(getListValidationsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListValidationsKey = (params: ListValidationsParams) =>
  [`/api/v1/validations`, ...(params ? [params] : [])] as const;

export type ListValidationsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listValidations>>
>;

/**
 * @summary List the validations of a subchapter
 */
export const useListValidations = <TError = Error>(
  params: ListValidationsParams,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof listValidations>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey =
    swrOptions?.swrKey ??
    (() => (isEnabled ? getListValidationsKey(params) : null));
  const swrFn = () => listValidations(params, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
export const getGetValidationUrl = (id: string) => {
  return `/api/v1/validations/${id}`;
};

/**
 * The state of one submission and, once graded, its verdict. Poll it after POST /api/v1/validations until the status is completed or failed.
 * @summary Show a validation
 */
export const getValidation = async (
  id: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<Validation> => {
  return apiFetch<Validation>(getGetValidationUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetValidationKey = (id: string) =>
  [`/api/v1/validations/${id}`] as const;

export type GetValidationQueryResult = NonNullable<
  Awaited<ReturnType<typeof getValidation>>
>;

/**
 * @summary Show a validation
 */
export const useGetValidation = <TError = Error>(
  id: string,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof getValidation>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled =
    swrOptions?.enabled !== false && id !== null && id !== undefined;
  const swrKey =
    swrOptions?.swrKey ?? (() => (isEnabled ? getGetValidationKey(id) : null));
  const swrFn = () => getValidation(id, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
